'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { ReactNode, useEffect, useState } from 'react';

interface CustomCollapseProps {
    title: string | ReactNode;
    children: ReactNode;
    defaultOpen?: boolean;
    open?: boolean;
    onToggle?: (isOpen: boolean) => void;
    headerClassName?: string;
    contentClassName?: string;
    iconClassName?: string;
    borderClassName?: string;
    headerBgColor?: string;
    contentBgColor?: string;
    titleClassName?: string;
    icon?: ReactNode;
    showBorder?: boolean;
    rounded?: string;
    disabledToggle?: boolean;
}

const CustomCollapse = ({
    title,
    children,
    defaultOpen = false,
    open,
    onToggle,
    headerClassName = '',
    contentClassName = '',
    iconClassName = '',
    borderClassName = 'border-gray-200',
    headerBgColor = 'bg-white hover:bg-gray-100',
    contentBgColor = 'bg-gray-50',
    titleClassName = 'font-medium text-gray-900',
    icon = null,
    showBorder = true,
    rounded = 'rounded-lg',
    disabledToggle = false
}: CustomCollapseProps) => {
    const [isOpenInternal, setIsOpenInternal] = useState<boolean>(defaultOpen);

    const isControlled = open !== undefined;
    const isOpen = isControlled ? open : isOpenInternal;

    useEffect(() => {
        if (isControlled && open !== undefined) {
            setIsOpenInternal(open);
        }
    }, [open, isControlled]);

    const handleToggle = () => {
        if (disabledToggle) return;

        const newState = !isOpen;

        if (!isControlled) {
            setIsOpenInternal(newState);
        }

        onToggle?.(newState);
    };

    return (
        <div className={`${showBorder ? `border ${borderClassName}` : ''} ${rounded} overflow-hidden`}>
            <button
                onClick={handleToggle}
                className={`flex w-full items-center justify-between p-4 transition-colors ${headerBgColor} ${headerClassName}`}
            >
                <span className={titleClassName}>{title}</span>

                {!disabledToggle &&
                    (icon || (
                        <motion.span
                            // animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.25 }}
                            className={`inline-block text-3xl text-gray-500 ${iconClassName}`}
                        >
                            {isOpen ? '−' : '+'}
                        </motion.span>
                    ))}
            </button>

            <AnimatePresence initial={false}>
                {isOpen && !disabledToggle && (
                    <motion.div
                        key='content'
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className='overflow-hidden'
                    >
                        <div
                            className={`p-4 ${contentBgColor} ${
                                showBorder ? `border-t ${borderClassName}` : ''
                            } ${contentClassName}`}
                        >
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CustomCollapse;

